<template>
  <v-card>
    <v-navigation-drawer
      color="secondary"
      :order="-1"
      permanent
    >
      <v-list-item class="pt-10 px-5">
        <logo />
      </v-list-item>

      <v-list v-model:opened="open" class="px-5 pt-10">
        <template v-for="sidebarRoute in routes" :key="sidebarRoute.name">
          <nav-bar-side-item-group
            v-if="sidebarRoute.children"
            :visible="sidebarRoute.condition"
            :group-name="sidebarRoute.groupName"
            :children="sidebarRoute.children"
            :icon="sidebarRoute.icon"
            :selected-icon="sidebarRoute.selectedIcon"
            :title="sidebarRoute.title"
            :badge="sidebarRoute.badge"
            :badge-color="sidebarRoute.badgeColor"
            @activate="activateGroup"
          />

          <nav-bar-side-item
            v-else
            :visible="sidebarRoute.condition"
            :route-name="sidebarRoute.name"
            :icon="sidebarRoute.icon"
            :selected-icon="sidebarRoute.selectedIcon"
            :title="sidebarRoute.title"
            :badge="sidebarRoute.badge"
            :badge-color="sidebarRoute.badgeColor"
            :data-testid="'nav-' + sidebarRoute.name"
          />
        </template>
      </v-list>

      <template #append>
        <div class="pa-5">
          <nav-bar-side-item
            v-if="hasDeferredPrompt"
            icon="mdi-download"
            :title="$t('installApp')"
            class="pa-5"
            @click.stop="showInstallPrompt"
          />
          <nav-bar-side-item
            icon="mdi-logout"
            :title="$t('logout')"
            class="pa-5"
            @click.stop="logout"
          />
        </div>
      </template>
    </v-navigation-drawer>
  </v-card>
</template>

<script setup>
import Logo from '@/components/PortalLogo';
import { useUserStore } from '@/store/user';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useNotificationStore } from '@/store/notifications';
import { computed, ref } from 'vue';
import NavBarSideItemGroup from '@/components/NavBarSideItemGroup.vue';
import NavBarSideItem from '@/components/NavBarSideItem.vue';
import { useMonitoringGeneralStore } from '@/store/monitoringGeneral';
import { useAppStore } from '@/store/store';
import { usePwaStore } from '@/store/pwa';

const i18n = useI18n();

const appStore = useAppStore();
const { feature } = appStore;
const { hasSocialReviewLinks } = storeToRefs(appStore);

const userStore = useUserStore();
const { logoutUser } = userStore;

const pwaStore = usePwaStore();
const { showInstallPrompt } = pwaStore;
const { hasDeferredPrompt } = storeToRefs(pwaStore);

const { hasNewNotifications } = storeToRefs(useNotificationStore());
const monitoringStore = useMonitoringGeneralStore();
const { monitoringEnabled, currentDeviceIntegrationIsFlexio } = storeToRefs(monitoringStore);

const open = ref(['monitoring']);

const routes = computed(() => ([
  {
    name: 'installations',
    icon: '$installations',
    selectedIcon: '$installations_selected_sidebar',
    title: i18n.t('bottomNav.installations'),
  },
  {
    name: 'documentsIndex',
    icon: 'mdi-clipboard-text',
    selectedIcon: 'mdi-clipboard-text',
    title: i18n.t('bottomNav.documents'),
  },
  {
    name: 'loyalty',
    icon: 'mdi-gift-outline',
    selectedIcon: 'mdi-gift-outline',
    title: i18n.t('bottomNav.loyalty'),
    condition: feature('loyalty'),
  },
  {
    groupName: 'monitoring',
    icon: '$monitoring',
    selectedIcon: '$monitoring_selected',
    title: i18n.t('bottomNav.monitoring'),
    condition: monitoringEnabled.value,
    children: [
      {
        name: 'monitoring',
        icon: '$monitoring',
        selectedIcon: '$monitoring_selected',
        title: i18n.t('bottomNav.monitoring'),
      },
      {
        name: 'insights',
        icon: 'mdi-plus-circle-multiple-outline',
        selectedIcon: 'mdi-plus-circle-multiple-outline',
        title: i18n.t('bottomNav.insights'),
        condition: currentDeviceIntegrationIsFlexio.value,
      },
      {
        name: 'history',
        icon: 'mdi-history',
        selectedIcon: 'mdi-history',
        title: i18n.t('bottomNav.history'),
      },
      {
        name: 'ev',
        icon: 'mdi-car-electric',
        selectedIcon: 'mdi-car-electric',
        title: i18n.t('bottomNav.ev'),
        condition: currentDeviceIntegrationIsFlexio.value && feature('monitoring.ev'),
      },
    ],
  },
  {
    name: 'support',
    icon: '$support',
    selectedIcon: '$support_selected',
    title: i18n.t('bottomNav.support'),
    condition: feature('support'),
  },
  {
    name: 'account',
    icon: '$account',
    selectedIcon: '$account_selected',
    title: i18n.t('bottomNav.account'),
  },
  {
    name: 'notifications',
    icon: 'mdi-bell-outline',
    selectedIcon: 'mdi-bell-outline',
    title: i18n.t('bottomNav.notifications'),
    badge: hasNewNotifications.value,
  },
  {
    name: 'reviews',
    icon: 'mdi-star',
    selectedIcon: 'mdi-star',
    title: i18n.t('bottomNav.reviews'),
    condition: feature('reviews') && hasSocialReviewLinks.value,
  },
]));

const activateGroup = (group) => {
  open.value.push(group);
};

function logout() {
  logoutUser();
}

</script>
